// The second-charge guard, as the widget shows it (paystack-initialize and
// redeem_and_confirm_cart, 20260928090040).
//
// Before a Paystack page is handed out the server asks whether an earlier
// payment for the same bookings went through, is still going through, or
// cannot be checked, and then starts nothing. The widget says so as a NOTICE,
// never as a red error next to a live Pay button:
//   * went through: Pay goes, and the widget watches that earlier payment
//     until the webhook's confirmation is on file, then shows it;
//   * still going / cannot check: the sentence, and Pay asks again.
// It also answers two questions the checkout could not before:
//   * a hold that "ran out" may have been paid in that very window, so the
//     widget asks the return-page check about the payment it last sent the
//     guest to before it says "your hold ended";
//   * a gift card or voucher that covers the cart is refused while a card
//     payment for it may have gone through (earlier_payment_unverified): on
//     this cart, or the same guest's for the same slot on a cart a reload
//     left behind. The widget asks the return-page check about them (scope
//     'redeem': the server finds them and records Paystack's answers; it
//     never names another cart's payment) and, when none was paid, confirms
//     again.
// And one it can offer to pass: the same guest already paid for the same
// slot on a cart a reload left behind (same_slot_already_paid, never naming
// that payment). The notice says so and that continuing makes a SECOND
// booking, with "Book another": that press asks again with the allow flag
// (allow_same_slot / p_allow_same_slot), which the server honours for this
// one reason only.
//
// The sentences are the server's own (charge-guard-copy.test.ts fails when
// they drift); these copies are only the fallback when the answer has none.
import { supabase } from "../config";
import type { CheckoutDetails } from "./checkout-steps";
import { fetchPaystackReturnConfirmation, type PaystackReturnConfirmation } from "./paystack-return";
import {
  REFUNDED_BOOKED_HINT,
  REFUNDED_HINT,
  REFUNDED_MESSAGE,
  confirmationOutcome,
  refundInFull,
  refundMessage,
} from "./confirmation-outcome";
import { PAYMENT_STATUS_TIMEOUT_MS, readCheckoutDetails } from "./payment-return";

export type ChargeGuardCode =
  | "earlier_payment_succeeded"
  | "earlier_payment_in_progress"
  | "payment_check_unavailable"
  | "same_slot_already_paid"
  // The widget's own, never the server's: the payment that "went through" is
  // being refunded (paid after the hold ended, or a second payment for a
  // booking already paid for). Set by the watch below, from the confirmation
  // reader (20261001160010).
  | "earlier_payment_refunded"
  // The widget's own too: the payment that "went through" was made on a page
  // opened before the booking changed (payment_superseded, 20261003200010).
  // It is refunded in full and pays for nothing, so Pay stays: the booking
  // is paid at its current total, here.
  | "earlier_payment_superseded";

export const CHARGE_GUARD_FALLBACK: Record<ChargeGuardCode, string> = {
  earlier_payment_succeeded:
    "Your earlier payment went through, so we haven't charged you again. You'll hear from us by email or WhatsApp shortly — please don't pay again.",
  earlier_payment_in_progress:
    "Your earlier payment is still going through, so we haven't started another one. Please wait a few minutes and check your email or WhatsApp before trying again.",
  payment_check_unavailable:
    "We couldn't check whether your earlier payment went through, so we haven't started a new one — we never want to charge you twice. Please check your email or WhatsApp, or try again in a few minutes. If you're still unsure, contact the venue.",
  same_slot_already_paid:
    "You already have a paid booking for this at the same time — please check your email or WhatsApp for it. If you continue, you'll make a second, separate booking.",
  earlier_payment_refunded: `${REFUNDED_MESSAGE} ${REFUNDED_HINT}`,
  earlier_payment_superseded:
    "Your earlier payment was made on a payment page opened before your booking changed, so it is being refunded in full. Please pay the updated total below.",
};

export type ChargeGuardNotice = {
  code: ChargeGuardCode;
  message: string;
  /** The earlier payment it is about, when the server named one. */
  earlierReference: string | null;
  /** earlier_payment_refunded: the booking stands (another payment paid for it). */
  bookingConfirmed?: boolean;
};

export function isChargeGuardCode(code: unknown): code is ChargeGuardCode {
  return typeof code === "string" && Object.prototype.hasOwnProperty.call(CHARGE_GUARD_FALLBACK, code);
}

/** A guard refusal as the notice the checkout shows, or null for any other code. */
export function chargeGuardNotice(
  code: unknown,
  message?: unknown,
  earlierReference?: unknown,
): ChargeGuardNotice | null {
  if (!isChargeGuardCode(code)) return null;
  return {
    code,
    message: typeof message === "string" && message.trim() ? message.trim() : CHARGE_GUARD_FALLBACK[code],
    earlierReference: typeof earlierReference === "string" && earlierReference ? earlierReference : null,
  };
}

/**
 * Pay is not offered again once the server says the earlier payment went
 * through, nor once that payment turns out to be refunded (its booking could
 * not be kept: the guest chooses another time from their visit).
 */
export function payBlockedByGuard(notice: ChargeGuardNotice | null): boolean {
  return notice?.code === "earlier_payment_succeeded" || notice?.code === "earlier_payment_refunded";
}

/** The earlier payment went through: watch it for its confirmation (or its refund). */
export function watchesEarlierPayment(notice: ChargeGuardNotice | null): boolean {
  return notice?.code === "earlier_payment_succeeded";
}

/**
 * The payment that "went through" is being refunded: the notice that says so
 * in plain words. With the booking still standing (another payment paid for
 * it), that it is confirmed; otherwise that it could not be kept.
 */
export function refundedNotice(
  bookingConfirmed: boolean,
  earlierReference: string | null = null,
  inFull = true,
): ChargeGuardNotice {
  return {
    code: "earlier_payment_refunded",
    message: bookingConfirmed
      ? `${refundMessage(true, inFull)} ${REFUNDED_BOOKED_HINT}`
      : inFull
        ? CHARGE_GUARD_FALLBACK.earlier_payment_refunded
        : `${refundMessage(false, false)} ${REFUNDED_HINT}`,
    earlierReference,
    bookingConfirmed,
  };
}

/**
 * The payment that "went through" was made on a page opened before the
 * booking changed (20261003200010): refunded in full, it paid for nothing.
 * Said plainly, and Pay stays (never "please don't pay again"): unless
 * another payment paid for the booking, which is the refunded-and-booked
 * notice instead.
 */
export function supersededNotice(
  answer: PaystackReturnConfirmation,
  earlierReference: string | null = null,
): ChargeGuardNotice {
  if (!answer.ok && answer.booking_confirmed === true && answer.booking_paid === true) {
    return refundedNotice(true, earlierReference, true);
  }
  return {
    code: "earlier_payment_superseded",
    message: CHARGE_GUARD_FALLBACK.earlier_payment_superseded,
    earlierReference,
  };
}

/**
 * The guest already paid for this slot on another cart: Pay steps aside for
 * the notice's own "Book another" (a deliberate second booking).
 */
export function offersBookAnother(notice: ChargeGuardNotice | null): boolean {
  return notice?.code === "same_slot_already_paid";
}

// ── The page this tab last sent the guest to ───────────────────────────────
// Kept in memory only: a browser Back from Paystack restores this very page
// (bfcache) with it, and a reload starts a new cart anyway (the server's
// guest-and-slot match covers that one).
let lastHop: string | null = null;
export function rememberPaystackHop(reference: string | null | undefined): void {
  if (typeof reference === "string" && reference) lastHop = reference;
}
export function lastPaystackHop(): string | null {
  return lastHop;
}

// ── The return-page check (paystack-payment-status) ────────────────────────
// Also what the return from Paystack asks first (useCheckoutReturn, with the
// cart token the tab kept with the trip): paid, still going, or not paid, at
// once, instead of waiting for the webhook.
export type PaymentState = "confirmed" | "paid" | "in_progress" | "not_paid" | "unknown";

// A check that has not answered in PAYMENT_STATUS_TIMEOUT_MS is a failed
// check (unknown): above the server's own wait for Paystack
// (state/payment-return.ts, pinned against the server's budget).
export { PAYMENT_STATUS_TIMEOUT_MS };

/**
 * The return-page check's answer: the state, and the server's reason when it
 * gave one (refunded; checking: another request is asking Paystack right
 * now; …) and whether it was served from the last Paystack answer
 * (cached: under 5 seconds old). A check that failed, timed out or was
 * refused (a 404: not this tab's payment) is unknown / "check_failed", never
 * "not paid". `details`: what the unpaid payment holds of the guest's
 * Special requests and a stay's Country (with "not paid" only; null: none
 * sent, as from an older server).
 */
export async function readPaymentStatus(
  reference: string,
  cartToken: string,
): Promise<{ state: PaymentState; reason: string | null; cached: boolean; details: CheckoutDetails | null }> {
  const failed = { state: "unknown" as const, reason: "check_failed", cached: false, details: null };
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), PAYMENT_STATUS_TIMEOUT_MS);
    });
    const reply = await Promise.race([
      supabase.functions.invoke("paystack-payment-status", { body: { reference, cart_token: cartToken } }),
      timeout,
    ]);
    if (!reply) return failed;
    const { data, error } = reply;
    const state = (data as { state?: unknown } | null)?.state;
    if (error || typeof state !== "string") return failed;
    if (!["confirmed", "paid", "in_progress", "not_paid", "unknown"].includes(state)) return failed;
    const reason = (data as { reason?: unknown } | null)?.reason;
    return {
      state: state as PaymentState,
      reason: typeof reason === "string" && reason ? reason : null,
      cached: (data as { cached?: unknown } | null)?.cached === true,
      details: readCheckoutDetails((data as { checkout?: unknown } | null)?.checkout),
    };
  } catch {
    return failed;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function checkPaymentState(reference: string, cartToken: string): Promise<PaymentState> {
  return (await readPaymentStatus(reference, cartToken)).state;
}

/**
 * Before the checkout says "your hold ended" (hold_expired /
 * no_pending_bookings): was the page this tab sent the guest to paid in that
 * very window? Paid or confirmed: the notice that it went through (and the
 * confirmation follows). Still going: wait. Otherwise null, and the lapse
 * sentence stands.
 */
export async function explainLapse(cartToken: string): Promise<ChargeGuardNotice | null> {
  const reference = lastPaystackHop();
  if (!reference) return null;
  const state = await checkPaymentState(reference, cartToken);
  if (state === "confirmed" || state === "paid") {
    return chargeGuardNotice("earlier_payment_succeeded", null, reference);
  }
  if (state === "in_progress") return chargeGuardNotice("earlier_payment_in_progress", null, reference);
  return null;
}

/**
 * redeem_and_confirm_cart refused (earlier_payment_unverified): ask the
 * return-page check about every card payment in the way (scope 'redeem',
 * with the guest's details for the guest-and-slot match). Paid: that notice
 * (with the reference only when it is this cart's). Still going or cannot
 * tell: that notice. Paid for this slot on another cart (never named): the
 * "Book another" notice, unless the guest already pressed it. Not paid
 * (Paystack's answers are now on file): null, and the caller confirms again
 * once.
 */
export async function checkEarlierCardPayments(
  cartToken: string,
  guest: { email?: string | null; phone?: string | null },
  { allowSameSlot = false }: { allowSameSlot?: boolean } = {},
): Promise<ChargeGuardNotice | null> {
  let state: PaymentState = "unknown";
  let reference: string | null = null;
  let reason: string | null = null;
  let message: string | null = null;
  try {
    const { data, error } = await supabase.functions.invoke("paystack-payment-status", {
      body: {
        scope: "redeem",
        cart_token: cartToken,
        guest_email: guest.email ?? null,
        guest_phone: guest.phone ?? null,
      },
    });
    const s = (data as { state?: unknown } | null)?.state;
    if (!error && typeof s === "string" && ["confirmed", "paid", "in_progress", "not_paid", "unknown"].includes(s)) {
      state = s as PaymentState;
    }
    const r = (data as { reference?: unknown } | null)?.reference;
    if (typeof r === "string" && r) reference = r;
    const why = (data as { reason?: unknown } | null)?.reason;
    if (typeof why === "string") reason = why;
    const m = (data as { message?: unknown } | null)?.message;
    if (typeof m === "string") message = m;
  } catch {
    state = "unknown";
  }
  // Nothing in flight is in the way, but this guest's slot is already paid
  // on another cart (never named): the notice with "Book another", or, once
  // the guest chose that, nothing more in the way (the confirm is sent again
  // with the allow flag).
  if (state === "paid" && reason === "same_slot_already_paid") {
    return allowSameSlot ? null : chargeGuardNotice("same_slot_already_paid", message);
  }
  if (state === "confirmed" || state === "paid") return chargeGuardNotice("earlier_payment_succeeded", null, reference);
  if (state === "in_progress") return chargeGuardNotice("earlier_payment_in_progress", null, reference);
  if (state !== "not_paid") return chargeGuardNotice("payment_check_unavailable");
  return null;
}

type Confirmed = Extract<PaystackReturnConfirmation, { ok: true }>;

/**
 * "It went through": watch that earlier payment until its confirmation is on
 * file (the webhook records it), then hand it over. The confirmation reader
 * says Confirmed only for a confirmed booking (20261001160010): a payment
 * the webhook is refunding instead (paid after the hold ended) is handed to
 * onRefunded, and the watch stops. A payment made on a page opened before
 * the booking changed (payment_superseded, 20261003200010) is handed to
 * onSuperseded and the watch stops too: it will never confirm, and "please
 * don't pay again" must not stand while the hold runs out. Every 4 seconds
 * for up to 3 minutes; returns the stop function.
 */
export function watchEarlierPayment(
  reference: string,
  onConfirmed: (result: Confirmed) => void,
  {
    everyMs = 4000,
    forMs = 180_000,
    onRefunded,
    onSuperseded,
  }: {
    everyMs?: number;
    forMs?: number;
    onRefunded?: (bookingConfirmed: boolean, inFull: boolean) => void;
    onSuperseded?: (answer: PaystackReturnConfirmation) => void;
  } = {},
): () => void {
  let stopped = false;
  const started = Date.now();
  let timer: ReturnType<typeof setTimeout> | null = null;
  const tick = async () => {
    if (stopped) return;
    const result = await fetchPaystackReturnConfirmation(reference);
    if (stopped) return;
    const outcome = confirmationOutcome(result);
    if (outcome === "confirmed" && result.ok) {
      stopped = true;
      onConfirmed(result);
      return;
    }
    if (outcome === "refunded" || outcome === "refunded_booked") {
      stopped = true;
      onRefunded?.(outcome === "refunded_booked", refundInFull(result));
      return;
    }
    if (outcome === "superseded") {
      stopped = true;
      onSuperseded?.(result);
      return;
    }
    if (outcome === "not_a_booking") {
      stopped = true;
      return;
    }
    if (Date.now() - started < forMs) timer = setTimeout(tick, everyMs);
  };
  timer = setTimeout(tick, 0);
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}
